import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Search, FilterList, Clear, CheckCircle } from '@mui/icons-material';
import { apiAppFetch } from '../utils/apiFetch';
import './../BlockedScripts.css';
import './../QuantitySetting.css';

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return Object.values(data);
  return [];
};

const QuantitySetting = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState('');
  const [settings, setSettings] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [error, setError] = useState('');
  const [scriptSearch, setScriptSearch] = useState('');

  const loadSettings = async (segment) => {
    if (!segment) {
      setSettings([]);
      return;
    }

    try {
      setLoadingSettings(true);
      setError('');
      const response = await apiAppFetch('getCustomQuantitySetting', {
        method: 'POST',
        body: JSON.stringify({ segment }),
      });

      if (response.status === 401) {
        navigate('/login', { replace: true });
        return;
      }

      const data = await response.json();
      setSettings(normalizeArray(data));
    } catch (fetchError) {
      setError('Unable to load quantity settings right now.');
      setSettings([]);
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => {
    const loadSegments = async () => {
      try {
        setLoadingSegments(true);
        setError('');
        const response = await apiAppFetch('segments', { method: 'GET' });

        if (response.status === 401) {
          navigate('/login', { replace: true });
          return;
        }

        const data = await response.json();
        const normalizedSegments = normalizeArray(data);
        setSegments(normalizedSegments);

        if (normalizedSegments.length > 0) {
          const defaultSegment = normalizedSegments[0].segment;
          setSelectedSegment(defaultSegment);
          loadSettings(defaultSegment);
        }
      } catch (fetchError) {
        console.error('Error fetching segments:', fetchError);
        setError('Unable to load segments right now.');
      } finally {
        setLoadingSegments(false);
      }
    };

    loadSegments();
  }, []);

  const handleSegmentChange = async (event) => {
    const segment = event.target.value;
    setSelectedSegment(segment);
    setScriptSearch('');
    await loadSettings(segment);
  };

  const filteredSettings = useMemo(() => {
    const searchValue = scriptSearch.trim().toLowerCase();
    if (!searchValue) return settings;

    return settings.filter((item) => {
      const scriptName = String(item?.dispname || '').toLowerCase();
      const lotVariant = String(item?.lotVariant || '').toLowerCase();
      return scriptName.includes(searchValue) || lotVariant.includes(searchValue);
    });
  }, [settings, scriptSearch]);

  return (
    <div className={`blocked-scripts-page quantity-setting-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="controls-section">
        <div className="search-container-blocked-scripts">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search quantity scripts..."
            value={scriptSearch}
            onChange={(event) => setScriptSearch(event.target.value)}
            className="search-input-blocked-scripts"
            disabled={!selectedSegment}
          />
          {scriptSearch && (
            <button className="clear-search" onClick={() => setScriptSearch('')}>
              <Clear />
            </button>
          )}
        </div>

        <div className="filter-container">
          <FilterList className="filter-icon" />
          <select
            value={selectedSegment}
            onChange={handleSegmentChange}
            className="filter-select"
            disabled={loadingSegments || segments.length === 0}
          >
            {segments.map((segment) => (
              <option key={segment.segment} value={segment.segment}>
                {segment.segment_name || segment.segment}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="content-section">
        {(loadingSegments || loadingSettings) && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading quantity settings...</p>
          </div>
        )}

        {!loadingSegments && !loadingSettings && error && (
          <div className="empty-state">
            <h3>Unable to Load Data</h3>
            <p>{error}</p>
          </div>
        )}

        {!loadingSegments && !loadingSettings && !error && filteredSettings.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <CheckCircle />
            </div>
            <h3>No Quantity Settings Found</h3>
            <p>
              {scriptSearch
                ? 'No scripts match your current search criteria.'
                : 'No quantity settings found for this segment.'}
            </p>
          </div>
        )}

        {!loadingSegments && !loadingSettings && !error && filteredSettings.length > 0 && (
          <div className="scripts-grid">
            {filteredSettings.map((item, index) => (
              <div key={`${item.dispname || 'symbol'}-${item.segment || selectedSegment}-${item.lotVariant || 'MEGA'}-${index}`} className="script-card">
                <div className="script-header">
                  <div className="script-info">
                    <h3 className="script-name">
                      {item.dispname || '-'}
                      {String(item.lotVariant || '').toUpperCase() === 'MINI' && (
                        <span className="lot-variant-mini">MINI</span>
                      )}
                    </h3>
                  </div>
                  <div className="exchange-badge" style={{ backgroundColor: '#2f67f0' }}>
                    {item.segment || selectedSegment}
                  </div>
                </div>

                <div className="script-details">
                  <div className="detail-row">
                    <span className="detail-label">Lot Per Position: </span>
                    <span className="detail-value">{item.max_order ?? '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Total Lot: </span>
                    <span className="detail-value">{item.positional_lot ?? '-'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuantitySetting;
